import type { ServerDetail } from '@/types/fleet';

/**
 * The three destructive per-server lifecycle actions (PR-E #3). Unlike the
 * curated actions in `server-actions.ts`, these do NOT go through the generic
 * Provisioning runner — they hit the dedicated Fleet lifecycle endpoints
 * (`POST /servers/:name/{decommission,quarantine,recover}`). Decommission
 * returns a Fleet `ActionResponse` polled via `useFleetTask`; quarantine and
 * recover are registry-only and answer `{ok: true}` (planet-proxy-KB#589).
 */
export type LifecycleActionKey = 'decommission' | 'quarantine' | 'recover';

export interface LifecycleActionDef {
  key: LifecycleActionKey;
  label: string;
  /** Path segment under `/servers/:name/`. */
  endpoint: LifecycleActionKey;
  /** decommission/quarantine require a reason; recover does not. */
  reasonRequired: boolean;
  /**
   * True when the endpoint applies the change at once and answers `{ok: true}`
   * with no Semaphore task (quarantine, recover). False when it returns a task
   * to track (decommission).
   */
  immediate: boolean;
  /**
   * When true the confirm dialog also gates on typing the server name
   * (decommission only — the most irreversible action).
   */
  requireNameConfirm: boolean;
  /** Confirm-dialog copy — plain language, no protocol guarantees. */
  confirmCopy: (server: ServerDetail) => string;
  /** Only shown when this predicate passes for the server. */
  visible: (server: ServerDetail) => boolean;
}

export const LIFECYCLE_ACTIONS: LifecycleActionDef[] = [
  {
    key: 'quarantine',
    label: 'Quarantine',
    endpoint: 'quarantine',
    reasonRequired: true,
    immediate: true,
    requireNameConfirm: false,
    confirmCopy: (s) =>
      `Quarantine ${s.name}. The server is taken out of rotation: no new connections land on it. Sessions already on it continue until they end, and nothing on the host is stopped or changed. This applies immediately — there is no playbook. Only Recover returns it to service.`,
    // Quarantine makes sense for a server that is currently in service.
    visible: (s) => s.status === 'active' || s.status === 'provisioning',
  },
  {
    key: 'recover',
    label: 'Recover',
    endpoint: 'recover',
    reasonRequired: false,
    immediate: true,
    requireNameConfirm: false,
    confirmCopy: (s) =>
      `Recover ${s.name} from quarantine and return it to active rotation. This applies immediately — there is no playbook.`,
    visible: (s) => s.status === 'quarantined',
  },
  {
    key: 'decommission',
    label: 'Decommission',
    endpoint: 'decommission',
    reasonRequired: true,
    immediate: false,
    requireNameConfirm: true,
    confirmCopy: (s) =>
      `Decommission ${s.name}. Containers are stopped and the host is cleaned up. This is irreversible — the server cannot be recovered once decommissioned. Status flips to "decommissioned" only once the playbook succeeds.`,
    visible: (s) => s.status !== 'decommissioned',
  },
];
